import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MailWarning } from "lucide-react";
import { toast } from "sonner";

export function EmailVerifyBanner() {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified !== false) return null;

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { code });
      await refresh();
      toast.success("Email verified — labs unlocked!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed.");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data?.message || "A new code is on its way.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not resend.");
    }
  };

  return (
    <div className="mb-6 border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/50 rounded-lg p-4" data-testid="email-verify-banner">
      <div className="flex flex-wrap items-center gap-3">
        <MailWarning className="w-5 h-5 text-amber-600 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-amber-900 dark:text-amber-200 text-sm">Verify your email to launch labs</div>
          <div className="text-xs text-amber-700 dark:text-amber-300/80">We emailed a 6-digit code to {user.email}. Enter it to unlock access.</div>
        </div>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          inputMode="numeric"
          maxLength={6}
          placeholder="------"
          className="w-28 font-mono tracking-widest bg-white"
          data-testid="banner-verify-code-input"
        />
        <Button size="sm" onClick={verify} disabled={busy || code.length < 6} data-testid="banner-verify-button">
          {busy ? "Verifying…" : "Verify"}
        </Button>
        <Button size="sm" variant="outline" onClick={resend} data-testid="banner-resend-button">Resend</Button>
      </div>
    </div>
  );
}
